import './DetailInfo.css';
import React, { forwardRef, useImperativeHandle, useState, useRef } from 'react';
import Gallery from './Gallery';

import svgPricePerSqm from './assets/graphics/price-per-sq-m.svg';
import svgTotalArea from './assets/graphics/total-area.svg';
import svgWaterFront from './assets/graphics/water-front.svg';
import svgOceanView from './assets/graphics/ocean-view.svg';
import svgBayView from './assets/graphics/bay-view.svg';
import svgInterior from './assets/graphics/interior.svg';
import svgExterior from './assets/graphics/exterior.svg';
import svgNumOfBeds from './assets/graphics/num-of-beds.svg';
import svgNumOfBaths from './assets/graphics/num-of-baths.svg';
import svgLevel from './assets/graphics/level.svg';
import svgNumOfParking from './assets/graphics/num-of-parking.svg';

const DetailInfo = forwardRef((props, ref) => {
    const { isVisible, itemInfo, toggleComponent } = props;

    useImperativeHandle(ref, () => ({
        toggleVisibility: () => {},
    }));

    const [showGallery, setShowGallery] = useState(false);
    const [activeIndex, setActiveIndex] = useState(1);

    const touchStartX = useRef(null);
    const touchEndX = useRef(null);

    const handleTouchStart = (e) => {
        touchStartX.current = e.touches[0].clientX;
    };

    const handleTouchMove = (e) => {
        touchEndX.current = e.touches[0].clientX;
    };

    const handleTouchEnd = () => {
        if (touchStartX.current !== null && touchEndX.current !== null) {
            const distance = touchStartX.current - touchEndX.current;
            const swipeThreshold = 50;

            if (distance > swipeThreshold) nextImage();       // swipe left
            else if (distance < -swipeThreshold) prevImage(); // swipe right
        }

        touchStartX.current = null;
        touchEndX.current = null;
    };

    const nextImage = () => {
        setActiveIndex((prevIndex) => (prevIndex + 1) % itemInfo.media.length);
    };

    const prevImage = () => {
        setActiveIndex((prevIndex) => (prevIndex - 1 + itemInfo.media.length) % itemInfo.media.length);
    };

    const getImageClass = (index) => {
        if (index === activeIndex && index === 0) return 'carousel-image--zero';
        if (index === activeIndex) return 'carousel-image';
        if (index === activeIndex + 1 || index === activeIndex - 1) return 'carousel-image--frozen';
        return 'carousel-image--hidden';
    };

    const closeGallery = () => {
        setShowGallery(false);
    };

    if (!isVisible) return null;

    return (
        <div className="detail-info-container">
            {showGallery ? (
                <Gallery itemInfo={itemInfo} startIndex={activeIndex} closeGallery={closeGallery} />
            ) : (
                <>
                    <div className="container-header">
                        <button className="back-button" onClick={toggleComponent}>← Back</button>
                    </div>

                    <div className="container-title">
                        <span>{itemInfo.displayName}</span>
                    </div>

                    <div className="container-carousel">
                        <button className="carousel-button-left" onClick={prevImage}>
                            &#10094;
                        </button>
                        <button className="carousel-button-right" onClick={nextImage}>
                            &#10095;
                        </button>

                        <div className="carousel-image-list">
                            {itemInfo.media.map((_, index) => (
                                <div
                                    key={index}
                                    className={`carousel-image-list-btn ${index === activeIndex ? 'carousel-image-list-btn--active' : ''}`}
                                    onClick={() => setActiveIndex(index)}
                                />
                            ))}
                        </div>

                        <div
                            className="carousel-image-strip"
                            onTouchStart={handleTouchStart}
                            onTouchMove={handleTouchMove}
                            onTouchEnd={handleTouchEnd}
                        >
                            {itemInfo.media.map((image, index) => (
                                <img
                                    key={index}
                                    className={getImageClass(index)}
                                    src={image}
                                    alt={`Image ${index + 1}`}
                                    onClick={() => setShowGallery(true)}
                                />
                            ))}
                            <img
                                key={itemInfo.length}
                                className={'carousel-image--zero'}
                                src={''}
                                alt={`blank`}
                                onClick={() => setShowGallery(true)}
                            />
                        </div>
                    </div>

                    <div className="container-properties">
                        <div className="properties-item" style={{ opacity: itemInfo.waterFront === 'yes' ? 1.0 : 0.2 }}>
                            <img src={svgWaterFront} />
                            &nbsp;&nbsp;{itemInfo.waterFront ? "Water Front" : ""}
                        </div>

                        <div className="properties-item" style={{ opacity: itemInfo.oceanView === 'yes' ? 1.0 : 0.2 }}>
                            <img src={svgOceanView} />
                            &nbsp;&nbsp;{itemInfo.oceanView ? "Ocean View" : ""}
                        </div>

                        <div className="properties-item" style={{ opacity: itemInfo.bayView === 'yes' ? 1.0 : 0.2 }}>
                            <img src={svgBayView} />
                            &nbsp;&nbsp;{itemInfo.bayView ? "Bay View" : ""}
                        </div>

                        <div className="properties-item" style={{ opacity: itemInfo.pricePerSqm > 0 ? 1.0 : 0.2 }}>
                            <img src={svgPricePerSqm} />
                            &nbsp;&nbsp;{itemInfo.pricePerSqm > 0 ? `$${(itemInfo.pricePerSqm * 1.0).toLocaleString()}` : "-"}
                        </div>

                        <div className="properties-item" style={{ opacity: itemInfo.totalArea > 0 ? 1.0 : 0.2 }}>
                            <img src={svgTotalArea} />
                            &nbsp;&nbsp;{itemInfo.totalArea > 0 ? `${(itemInfo.totalArea * 1.0).toLocaleString()}m²` : "-"}
                        </div>

                        <div className="properties-item" style={{ opacity: itemInfo.interiorArea > 0 ? 1.0 : 0.2 }}>
                            <img src={svgInterior} />
                            &nbsp;&nbsp;{itemInfo.interiorArea > 0 ? `${(itemInfo.interiorArea * 1.0).toLocaleString()}m²` : "-"}
                        </div>

                        <div className="properties-item" style={{ opacity: itemInfo.exteriorArea > 0 ? 1.0 : 0.2 }}>
                            <img src={svgExterior} />
                            &nbsp;&nbsp;{itemInfo.exteriorArea > 0 ? `${(itemInfo.exteriorArea * 1.0).toLocaleString()}m²` : "-"}
                        </div>

                        <div className="properties-item" style={{ opacity: itemInfo.level > 0 ? 1.0 : 0.2 }}>
                            <img src={svgLevel} />
                            &nbsp;&nbsp;{itemInfo.level > 0 ? itemInfo.level : "-"}
                        </div>

                        <div className="properties-item" style={{ opacity: itemInfo.numberOfBedrooms > 0 ? 1.0 : 0.2 }}>
                            <img src={svgNumOfBeds} />
                            &nbsp;&nbsp;{itemInfo.numberOfBedrooms > 0 ? itemInfo.numberOfBedrooms : "-"}
                        </div>

                        <div className="properties-item" style={{ opacity: itemInfo.numberOfBathrooms > 0 ? 1.0 : 0.2 }}>
                            <img src={svgNumOfBaths} />
                            &nbsp;&nbsp;{itemInfo.numberOfBathrooms > 0 ? itemInfo.numberOfBathrooms : "-"}
                        </div>

                        <div className="properties-item" style={{ opacity: itemInfo.numberOfParkingSpaces > 0 ? 1.0 : 0.2 }}>
                            <img src={svgNumOfParking} />
                            &nbsp;&nbsp;{itemInfo.numberOfParkingSpaces > 0 ? itemInfo.numberOfParkingSpaces : "-"}
                        </div>

                        <div className="properties-item"></div>

                        <div className="price-container">
                            <div className="price-header">
                                <span className="price-label">Price</span>
                                <span className="price-value">${(itemInfo.totalPrice * 1.0).toLocaleString()}</span>
                            </div>
                        </div>
                    </div>

                    <div className="container-description">
                        <span>Description</span><br /><br />
                        <span className="description-text">{itemInfo.description}</span>
                    </div>
                </>
            )}
        </div>
    );
});

export default DetailInfo;
